import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

interface ApaRangeRow {
  label: string;
  min: number;
  max: number;
  value: number;
  currentValue: number;
}

type ApaRangeOverride = number | { min?: number; max?: number; value?: number };

@Component({
  selector: 'app-apa-ranges',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './apa-ranges.component.html',
  styleUrl: './apa-ranges.component.scss',
})
export class ApaRangesComponent {
  data: any;
  rows: ApaRangeRow[] = [];
  private allRows: ApaRangeRow[] = [];
  private filterSub?: Subscription;
  private dataSub?: Subscription;
  private readonly configService = inject(ConfigService);
  private readonly apaOverridesStorageKey = 'dvm_apa_range_overrides';

  constructor(
    private http: HttpClient,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.loadRows(this.globalFilterService.getCurrentFilters());

    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (this.shouldReloadOnFilter(filters)) {
        this.loadRows(filters);
        return;
      }

      this.applySelectedRowFilter();
    });
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.dataSub?.unsubscribe();
  }

  private extractRows(res: any): Array<{ label: string; min: number; max: number; value: number }> {
    const configuredRows = this.extractConfiguredRows(res);
    if (configuredRows.length) {
      return configuredRows;
    }

    if (Array.isArray(res?.responseList?.[0]?.data)) {
      return res.responseList[0].data;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    if (Array.isArray(res)) {
      return res;
    }
    return [];
  }

  private loadRows(filters: any = {}): void {
    const apiUrl = this.data?.apiUrl;
    if (!apiUrl) {
      const normalized = this.normalizeRows(this.extractRows(this.data));
      this.allRows = this.applySavedOverrides(normalized);
      this.applySelectedRowFilter();
      return;
    }

    this.dataSub?.unsubscribe();
    const method = this.data?.method || (this.isLocalAssetUrl(apiUrl) ? 'get' : 'post');
    this.dataSub = this.configService.getConfig(apiUrl, this.data, filters, method).subscribe((res) => {
      const normalized = this.normalizeRows(this.extractRows(res));
      this.allRows = this.applySavedOverrides(normalized);
      this.applySelectedRowFilter();
    });
  }

  private normalizeRows(rows: Array<{ label: string; min: number; max: number; value: number }>): ApaRangeRow[] {
    const configuredMin = this.getConfiguredBoundary('min');
    const configuredMax = this.getConfiguredBoundary('max');

    return rows.map((row) => {
      const rowMin = Number(row.min);
      const rowMax = Number(row.max);
      const min = Number.isFinite(rowMin) ? rowMin : 0;
      const max = Number.isFinite(rowMax) ? rowMax : min;
      const safeMin = configuredMin !== null ? Math.max(min, configuredMin) : min;
      const safeMax = configuredMax !== null ? Math.min(max, configuredMax) : max;
      const value = Number(row.value);
      const clampedMax = safeMax >= safeMin ? safeMax : safeMin;

      return {
        ...row,
        min: safeMin,
        max: clampedMax,
        value,
        currentValue: this.clampToRange(value, safeMin, clampedMax),
      };
    });
  }

  private shouldReloadOnFilter(filters: any): boolean {
    if (filters?.skipReload) {
      return false;
    }

    if (this.data?.reloadOnFilter === false) {
      return false;
    }

    return !!this.data?.apiUrl && !this.isLocalAssetUrl(this.data.apiUrl);
  }

  private isLocalAssetUrl(url: string): boolean {
    return String(url || '').startsWith('assets') || String(url || '').startsWith('/assets');
  }

  private extractConfiguredRows(source: any): Array<{ label: string; min: number; max: number; value: number }> {
    const responseSource = this.resolvePath(source, this.data?.responsePath || this.data?.dataPath || '');
    const record = this.asRecord(responseSource) || this.asRecord(source);
    const rowConfig = Array.isArray(this.data?.rangeRows) ? this.data.rangeRows : [];
    const keyMap = this.data?.rangeKeyMap || this.data?.valueKeyMap || {};

    if (!record || (!rowConfig.length && !Object.keys(keyMap).length)) {
      return [];
    }

    const labelConfigs = rowConfig.length
      ? rowConfig
      : Object.entries(keyMap).map(([label, valueKey]) => ({ label, valueKey }));

    return labelConfigs
      .map((config: any) => {
        const label = String(config?.label || '').trim();
        const valueKey = config?.valueKey || this.resolveMappedKey(label, keyMap);
        const minKey = config?.minKey || this.resolveLimitKey(label, valueKey, 'min');
        const maxKey = config?.maxKey || this.resolveLimitKey(label, valueKey, 'max');
        const min = Number(this.resolveRecordValue(record, minKey));
        const max = Number(this.resolveRecordValue(record, maxKey));
        const value = Number(this.resolveRecordValue(record, valueKey));

        return { label, min, max, value };
      })
      .filter((row: any) => row.label && Number.isFinite(row.min) && Number.isFinite(row.max));
  }

  private resolveLimitKey(label: string, valueKey: string, type: 'min' | 'max'): string {
    const map =
      type === 'min'
        ? this.data?.lowerLimitKeyMap || this.data?.lowerlimitKeyMap || this.data?.minKeyMap || {}
        : this.data?.upperLimitKeyMap || this.data?.upperlimitKeyMap || this.data?.maxKeyMap || {};
    const mapped = this.resolveMappedKey(label, map);
    if (mapped) {
      return mapped;
    }

    const prefixCandidates = type === 'min'
      ? ['lowerlimit', 'lower_limit', 'min']
      : ['upperlimit', 'upper_limit', 'max'];
    const normalizedValueKey = String(valueKey || '').trim();
    return normalizedValueKey ? `${prefixCandidates[0]}_${normalizedValueKey}` : '';
  }

  private resolveMappedKey(label: string, keyMap: Record<string, any>): string {
    const normalizedLabel = this.normalizeLabel(label);
    const matched = Object.entries(keyMap || {}).find(([sourceLabel]) => (
      this.normalizeLabel(sourceLabel) === normalizedLabel
    ));
    return String(matched?.[1] || '').trim();
  }

  private resolveRecordValue(record: Record<string, any>, key: string): any {
    if (!key) {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return record[key];
    }

    return this.resolvePath(record, key);
  }

  private resolvePath(source: any, path: string): any {
    if (!path) {
      return undefined;
    }

    return String(path).split('.').reduce((current, segment) => {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }

      if (typeof current === 'object') {
        return current[segment];
      }

      return undefined;
    }, source);
  }

  private asRecord(value: any): Record<string, any> | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  }

  getPositionPercent(row: { min: number; max: number; currentValue: number }): number {
    const range = row.max - row.min;
    if (!range) return 0;
    const pct = ((row.currentValue - row.min) / range) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  onSlide(row: ApaRangeRow, event: Event): void {
    const target = event.target as HTMLInputElement;
    row.currentValue = this.clampToRange(Number(target.value), row.min, row.max);
    const rowRef = this.findRowRef(row);
    if (rowRef) {
      rowRef.currentValue = row.currentValue;
      this.saveOverride(rowRef.label, rowRef.currentValue, rowRef.min, rowRef.max);
    } else {
      this.saveOverride(row.label, row.currentValue, row.min, row.max);
    }
  }

  onBoundaryChange(row: ApaRangeRow, key: 'min' | 'max'): void {
    const rowRef = this.findRowRef(row) || row;
    const min = Number(rowRef.min);
    const max = Number(rowRef.max);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return;
    }

    if (min > max) {
      if (key === 'min') {
        rowRef.max = min;
      } else {
        rowRef.min = max;
      }
    }

    rowRef.currentValue = this.clampToRange(Number(rowRef.currentValue), Number(rowRef.min), Number(rowRef.max));
    row.min = rowRef.min;
    row.max = rowRef.max;
    row.currentValue = rowRef.currentValue;
    this.saveOverride(rowRef.label, rowRef.currentValue, rowRef.min, rowRef.max);
  }

  getStep(row: { min: number; max: number }): number {
    const range = row.max - row.min;
    if (!range || range < 0) return 1;
    return range / 100;
  }

  formatDisplayValue(value: number): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '0.00';
    return numericValue.toFixed(2);
  }

  private clampToRange(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) return min;
    return Math.max(min, Math.min(max, value));
  }

  private getConfiguredBoundary(key: 'min' | 'max'): number | null {
    const value =
      this.data?.sliderBounds?.[key] ??
      this.data?.rangeBounds?.[key] ??
      this.data?.[key === 'min' ? 'sliderMin' : 'sliderMax'];
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private applySelectedRowFilter(): void {
    const showSelectedOnly = this.data?.showSelectedProductPriceTypeOnly === true;
    if (!showSelectedOnly) {
      this.rows = [...this.allRows];
      return;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const selectedRaw =
      filters?.requestParams?.['product_price_type'] ??
      filters?.queryParams?.['product_price_type'];
    const selected = Array.isArray(selectedRaw) ? selectedRaw[0] : selectedRaw;
    const normalizedSelected = this.normalizeLabel(selected);

    if (!normalizedSelected) {
      this.rows = [...this.allRows];
      return;
    }

    const filtered = this.allRows.filter((row) => this.normalizeLabel(row?.label) === normalizedSelected);
    this.rows = filtered.length ? filtered : [...this.allRows];
  }

  private normalizeLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private applySavedOverrides(rows: ApaRangeRow[]) {
    const overrides = this.readOverridesMap();
    if (!overrides) return rows;

    const configuredMin = this.getConfiguredBoundary('min');
    const configuredMax = this.getConfiguredBoundary('max');

    return rows.map((row) => {
      const key = this.normalizeLabel(row?.label);
      const override = overrides[key];
      if (override === undefined || override === null) {
        return row;
      }
      const overrideData = this.normalizeOverride(override);
      const rowMin = Number(row.min);
      const rowMax = Number(row.max);
      const min = Number.isFinite(Number(overrideData.min)) ? Number(overrideData.min) : rowMin;
      const max = Number.isFinite(Number(overrideData.max)) ? Number(overrideData.max) : rowMax;
      const safeMin = configuredMin !== null ? Math.max(min, configuredMin) : min;
      const safeMax = configuredMax !== null ? Math.min(max, configuredMax) : max;
      const clampedMax = safeMax >= safeMin ? safeMax : safeMin;
      const fallbackValue = Number(row.currentValue ?? row.value);
      const value = Number.isFinite(Number(overrideData.value)) ? Number(overrideData.value) : fallbackValue;
      const safeValue = this.clampToRange(value, safeMin, clampedMax);
      return { ...row, min: safeMin, max: clampedMax, currentValue: safeValue };
    });
  }

  private saveOverride(label: any, value: number, min?: number, max?: number): void {
    const key = this.normalizeLabel(label);
    if (!key) return;
    const map = this.readOverridesMap() || {};
    const previous = this.normalizeOverride(map[key]);
    map[key] = {
      min: Number.isFinite(Number(min)) ? Number(min) : previous.min,
      max: Number.isFinite(Number(max)) ? Number(max) : previous.max,
      value,
    };
    try {
      sessionStorage.setItem(this.apaOverridesStorageKey, JSON.stringify(map));
    } catch {
      // ignore storage failures
    }
  }

  private readOverridesMap(): Record<string, ApaRangeOverride> | null {
    try {
      const raw = sessionStorage.getItem(this.apaOverridesStorageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  private normalizeOverride(override: ApaRangeOverride | undefined): { min?: number; max?: number; value?: number } {
    if (override && typeof override === 'object') {
      return override;
    }

    if (Number.isFinite(Number(override))) {
      return { value: Number(override) };
    }

    return {};
  }

  private findRowRef(row: ApaRangeRow): ApaRangeRow | undefined {
    return this.allRows.find((r) => this.normalizeLabel(r?.label) === this.normalizeLabel(row?.label));
  }
}
